import reduceReducers from "reduce-reducers";
import { AnyAction, Reducer } from "redux";
import { fetchUiAppRegistration } from "../../api/SettingsApi";
import { setUpUser } from "../../api/UserApi";
import { IUserAuthenticator, UserAuthenticator } from "../../auth";
import { AppThunkAction } from "../ApplicationStore";
import { ActionCreators as languageActions, reducer as reducerLanguage } from "./SessionLanguage";
import { SessionState, UserType, unloadedState } from "./SessionState";
import { v4 as uuidv4 } from "uuid";
import { PublicClientApplication } from "@azure/msal-browser";
import { AppRegistration } from "../../model/AppRegistration";

interface SetAppRegistrationAction {
  type: "SET_APP_REGISTRATION";
  appRegistration: AppRegistration;
}
interface SetAuthClientAction {
  type: "SET_AUTH_CLIENT";
  authClient: PublicClientApplication;
}
interface SetAuthenticatorAction {
  type: "SET_AUTHENTICATOR";
  userAuthenticator: IUserAuthenticator;
}
interface LoginAction {
  type: "SET_USER_LOGGED_IN";
}
interface LogoutAction {
  type: "SET_USER_LOGGED_OUT";
}
interface RenewSessionIdAction {
  type: "GENERATE_SESSION_ID";
}

type KnownAction = LoginAction | LogoutAction | SetAuthenticatorAction | RenewSessionIdAction | SetAuthClientAction | SetAppRegistrationAction;

const LastUserTypeKey = "lastUserType";

const getAuthenticator = (typeOfUser: UserType): Promise<IUserAuthenticator> => {
  return fetchUiAppRegistration(typeOfUser).then(
    () => new UserAuthenticator() as IUserAuthenticator
  );
};

const setLoginStatusAndEnsureUserExists = (dispatch: (action: KnownAction) => void) => (isLoggedIn: boolean) => {
  if (isLoggedIn) {
    setUpUser();
    dispatch({ type: "SET_USER_LOGGED_IN" });
    languageActions.getAndSetLanguage();
  } else {
    dispatch({ type: "SET_USER_LOGGED_OUT" });
  }
};

export const getLastUserType = () => window.localStorage.getItem(LastUserTypeKey) as UserType | undefined;

export const ActionCreators = {
  checkLoginStatus: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
    try {
      const lastUserType = getLastUserType();

      if (!lastUserType) {
        dispatch({ type: "SET_USER_LOGGED_OUT" });
        return;
      }

      dispatch({ type: "GENERATE_SESSION_ID" });

      const userAuthenticator = await getAuthenticator(lastUserType);
      dispatch({ type: "SET_AUTHENTICATOR", userAuthenticator });
      const isAuthenticated = await userAuthenticator.IsAuthenticated();
      setLoginStatusAndEnsureUserExists(dispatch)(isAuthenticated);
    } catch (error) {
      console.log(error);
    }
  },
  setAuthenticationClient: (client: PublicClientApplication): AppThunkAction<KnownAction> => async (dispatch, getState) => {
    dispatch({ type: "SET_AUTH_CLIENT", authClient:client });
  },
  setAppRegistration: (appRegistration: AppRegistration): AppThunkAction<KnownAction> => async (dispatch, getState) => {
    dispatch({ type: "SET_APP_REGISTRATION", appRegistration });
  }
};

const reducerSession: Reducer<SessionState> = (state: SessionState = unloadedState, action: KnownAction) => {
  switch (action.type) {
    case "SET_APP_REGISTRATION":
      return {
        ...state,
        appRegistration: action.appRegistration,
      };
    case "SET_AUTH_CLIENT":
      return {
        ...state,
        authClient: action.authClient,
      };
    case "SET_AUTHENTICATOR":
      return {
        ...state,
        userAuthenticator: action.userAuthenticator,
      };
    case "SET_USER_LOGGED_IN":
      return {
        ...state,
        isUserAuthenticated: true,
        isLoadingAuth: false,
      };
    case "SET_USER_LOGGED_OUT": {
      return {
        ...unloadedState,
        language: state.language,
        isLoadingAuth: false,
      };
    }
    case "GENERATE_SESSION_ID":
      return {
        ...state,
        sessionId: uuidv4(),
      };

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      // eslint-disable-next-line
      const exhaustiveCheck: never = action;
  }

  return state;
};

export const reducer = reduceReducers(reducerSession, reducerLanguage) as Reducer<SessionState, AnyAction>;
